import React, { useEffect, useState } from 'react';
import { siteData } from './data';
import './App.css';

/* ── small SVG icons ── */
const icons = {
  github: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
    </svg>
  ),
  linkedin: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
      <rect x="2" y="9" width="4" height="12"/>
      <circle cx="4" cy="4" r="2"/>
    </svg>
  ),
  mail: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  download: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  externalLink: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  ),
};

function SectionLabel({ children }) {
  return (
    <div className="section-label">
      <span>{children}</span>
    </div>
  );
}

function App() {
  const [visible, setVisible] = useState(false);
  const { name, initials, title, tagline, about, resume, links, skills, skillsHeadline, projects, projectsHeadline, profilePhoto } = siteData;

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`page ${visible ? 'page--visible' : ''}`}>
      <main className="wrap" role="main">

        {/* ── Hero ── */}
        <header className="hero" style={{ '--delay': '0ms' }}>
          <div className="avatar" aria-hidden="true">
            {profilePhoto ? (
              <img src={profilePhoto} alt={name} />
            ) : (
              initials
            )}
          </div>
          <div className="hero-text">
            <p className="tagline">{title}</p>
            <h1>{name}</h1>
            <p className="hero-subtitle">{tagline}</p>
          </div>
        </header>

        {/* ── Resume ── */}
        <section className="section" style={{ '--delay': '80ms' }} aria-label="Resume">
          <SectionLabel>resume</SectionLabel>
          <a
            href={resume}
            target="_blank"
            rel="noopener noreferrer"
            className="resume-btn"
            aria-label="Download Arron's resume (opens in new tab)"
          >
            {icons.download}
            <span>Download Resume</span>
          </a>
        </section>

        {/* ── Skills ── */}
        <section className="section" style={{ '--delay': '160ms' }} aria-label="Skills">
          <SectionLabel>{skillsHeadline}</SectionLabel>
          <ul className="skills-grid" role="list">
            {skills.map((skill) => (
              <li key={skill} className="skill-tag">
                <span className="skill-dot" aria-hidden="true" />
                {skill}
              </li>
            ))}
          </ul>
        </section>
        {/* ── About ── */}
        <section className="section" style={{ '--delay': '160ms' }} aria-label="About me">
          <SectionLabel>about me</SectionLabel>
          <p className="about-intro">{about.intro}</p>
          <div className="values-grid" role="list">
            {about.values.map((value) => (
              <div key={value.title} className="value-card" role="listitem">
                <div className="value-content">
                  <h3 className="value-title">{value.title}</h3>
                  <p className="value-description">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Projects ── */}
        <section className="section" style={{ '--delay': '320ms' }} aria-label="Career highlights">
          <SectionLabel>career highlights</SectionLabel>
          <p className="projects-headline">{projectsHeadline}</p>
          <ul className="projects-list" role="list">
            {projects.map((p) => (
              <li key={p.name}>
                <div className="project-card">
                  {p.category && <div className="project-category">{p.category}</div>}
                  <div className="project-header">
                    <span className="project-name">{p.name}</span>
                    <span className="project-year">{p.year}</span>
                  </div>
                  <p className="project-desc">{p.description}</p>
                  <ul className="project-tags" role="list" aria-label="Technologies used">
                    {p.tags.map((tag) => (
                      <li key={tag} className="ptag">{tag}</li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Contact ── */}
        <section className="section" style={{ '--delay': '400ms' }} aria-label="Contact">
          <SectionLabel>get in touch</SectionLabel>
          <nav className="contact-row" aria-label="Social and contact links">
            {links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.href.startsWith('mailto') ? undefined : '_blank'}
                rel={link.href.startsWith('mailto') ? undefined : 'noopener noreferrer'}
                className="contact-btn"
              >
                {icons[link.icon]}
                <span>{link.label}</span>
              </a>
            ))}
          </nav>
        </section>

        {/* ── Footer ── */}
        <footer className="site-footer" style={{ '--delay': '480ms' }}>
          <span>{name}</span>
          <span className="footer-dot" aria-hidden="true">·</span>
          <span>{new Date().getFullYear()}</span>
        </footer>

      </main>
    </div>
  );
}

export default App;
